// Per-symptom statistics report (SYMPTOM_STATS_REPORT_PLAN.md).
// Pure, deterministic, client-side only (D-SS-7) — segments symptom/headache
// events into resolution-bounded sessions (D-SS-5) and aggregates per-symptom
// counters over a caller-supplied date range for the Reports page. No LLM
// call, no I/O, no gap-based auto-close (unlike itemReport.ts's
// episodeStartEvents(), which this deliberately does not reuse — see D-SS-5).

import type { MedicalEvent } from "./types.js";
import { symptomIdentityOf } from "./symptomFollowUp.js";
import { localCalendarDate } from "./datetime.js";

/** Cap on region/side/trigger breakdown entries per row (avoid unbounded lists from noisy free-text triggers). */
export const SYMPTOM_STATS_BREAKDOWN_TOP_N = 5;

// Mirrors AnalyzePane.tsx's VIEWER_TZ convention — this module is only ever
// invoked client-side (D-SS-7 has no Lambda path), so the runtime's resolved
// timezone is the viewer's local timezone.
const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export interface SymptomStatSession {
  startAt: string; // ISO — first check-in of this session
  resolvedAt?: string; // ISO — the resolution event's occurredAt, if closed
  ongoing: boolean; // true if not resolved by endMs (D-SS-1)
}

export interface SymptomStatBreakdownEntry {
  value: string; // e.g. "abdomen-upper", "left", "stress"
  count: number;
}

export interface SymptomStatRow {
  key: string; // symptomFollowUpKey identity
  name: string; // display name — most recent occurrence's wording
  eventType: "symptom" | "headache"; // for icon/label purposes only
  totalCheckIns: number; // count of events with severity >= 1 in window (D-SS-6)
  sessionCount: number; // total sessions (open + closed) touched in window
  resolvedSessionCount: number;
  ongoingSessionCount: number;
  resolvedPct: number; // Math.round(resolvedSessionCount / sessionCount * 100)
  minSeverity: number; // across all severity>=1 check-ins
  maxSeverity: number;
  avgSeverity: number; // rounded to 1 decimal
  distinctDaysLogged: number; // distinct local calendar dates with >=1 check-in
  minResolutionMs?: number; // across closed sessions only; undefined if none closed
  maxResolutionMs?: number;
  avgResolutionMs?: number;
  firstAt: string; // earliest in-window event for this key
  lastAt: string; // latest in-window event for this key
  regionBreakdown: SymptomStatBreakdownEntry[]; // symptom.region + headache.location, sorted desc by count, top 5
  sideBreakdown: SymptomStatBreakdownEntry[]; // symptom.side only, sorted desc, top 5
  triggerBreakdown: SymptomStatBreakdownEntry[]; // symptom.triggers[] + headache.triggersUserNoted[], sorted desc, top 5
}

/**
 * Extract the follow-up identity key + display name from a symptom or
 * headache event. Delegates identity derivation to symptomFollowUp.ts's
 * symptomIdentityOf() (Task 1.3); keeps this module's "Headache" display
 * capitalization for the report's row label.
 */
function extractKey(e: MedicalEvent): { key: string; name: string } | null {
  const id = symptomIdentityOf(e);
  if (!id) return null;
  return { key: id.key, name: id.eventType === "headache" ? "Headache" : id.name };
}

/** D-SS-5 resolution sentinel: severity 0, or resolvedAt set. */
function isResolutionEvent(e: MedicalEvent): boolean {
  const p = e.payload as Record<string, unknown>;
  return (typeof p.severity === "number" && p.severity === 0) || typeof p.resolvedAt === "string";
}

function tallyValue(tally: Map<string, { value: string; count: number }>, raw: unknown): void {
  if (typeof raw !== "string") return;
  const trimmed = raw.trim();
  if (!trimmed) return;
  const dedupeKey = trimmed.toLowerCase();
  const existing = tally.get(dedupeKey);
  if (existing) {
    existing.count += 1;
  } else {
    tally.set(dedupeKey, { value: trimmed, count: 1 });
  }
}

function topBreakdown(tally: Map<string, { value: string; count: number }>): SymptomStatBreakdownEntry[] {
  return [...tally.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, SYMPTOM_STATS_BREAKDOWN_TOP_N)
    .map((e) => ({ value: e.value, count: e.count }));
}

/**
 * Build per-symptom statistics for symptom/headache events with `occurredAt`
 * in [startMs, endMs]. Sessions are segmented purely by explicit resolution
 * (D-SS-5) — no gap/autoCloseDays branch. See SYMPTOM_STATS_REPORT_PLAN.md
 * for the full spec and the decisions (D-SS-1..11) this implements.
 */
export function buildSymptomStats(
  events: MedicalEvent[],
  startMs: number,
  endMs: number
): SymptomStatRow[] {
  interface Group {
    key: string;
    events: MedicalEvent[]; // chronological, oldest first
  }
  const groups = new Map<string, Group>();

  const inWindow = events
    .filter(
      (e) =>
        !e.deleted &&
        (e.eventType === "symptom" || e.eventType === "headache") &&
        Date.parse(e.occurredAt) >= startMs &&
        Date.parse(e.occurredAt) <= endMs
    )
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));

  for (const e of inWindow) {
    const extracted = extractKey(e);
    if (!extracted) continue;
    const g = groups.get(extracted.key);
    if (g) {
      g.events.push(e);
    } else {
      groups.set(extracted.key, { key: extracted.key, events: [e] });
    }
  }

  const rows: SymptomStatRow[] = [];

  for (const g of groups.values()) {
    const latest = g.events[g.events.length - 1];
    const latestExtracted = extractKey(latest)!;
    const eventType = latest.eventType as "symptom" | "headache";

    // Session segmentation (D-SS-5): a new session starts at the first event
    // in the group, or immediately after any resolution event.
    const sessions: SymptomStatSession[] = [];
    let sessionStart: string | null = null;
    for (const e of g.events) {
      if (sessionStart === null) sessionStart = e.occurredAt;
      if (isResolutionEvent(e)) {
        sessions.push({ startAt: sessionStart, resolvedAt: e.occurredAt, ongoing: false });
        sessionStart = null;
      }
    }
    if (sessionStart !== null) {
      // Last event wasn't a resolution -> still-open at window end (D-SS-1).
      sessions.push({ startAt: sessionStart, ongoing: true });
    }

    const resolvedSessions = sessions.filter((s) => !s.ongoing);
    const ongoingSessions = sessions.filter((s) => s.ongoing);

    // Severity + check-in counting (D-SS-2, D-SS-6): every event with
    // severity >= 1 across the whole group, not per-session (skips the
    // severity-0 resolution sentinel).
    const severities: number[] = [];
    for (const e of g.events) {
      const p = e.payload as Record<string, unknown>;
      const severity = typeof p.severity === "number" ? p.severity : undefined;
      if (typeof severity === "number" && severity >= 1) severities.push(severity);
    }
    const totalCheckIns = severities.length;
    const minSeverity = totalCheckIns > 0 ? Math.min(...severities) : 0;
    const maxSeverity = totalCheckIns > 0 ? Math.max(...severities) : 0;
    const avgSeverity =
      totalCheckIns > 0 ? Math.round((severities.reduce((a, b) => a + b, 0) / totalCheckIns) * 10) / 10 : 0;

    // Distinct local calendar days with >=1 check-in.
    const days = new Set<string>();
    for (const e of g.events) {
      const p = e.payload as Record<string, unknown>;
      const severity = typeof p.severity === "number" ? p.severity : undefined;
      if (typeof severity === "number" && severity >= 1) {
        days.add(localCalendarDate(e.occurredAt, VIEWER_TZ));
      }
    }

    // Time-to-resolution (closed sessions only).
    const resolutionDurations = resolvedSessions.map(
      (s) => Date.parse(s.resolvedAt as string) - Date.parse(s.startAt)
    );
    const hasResolutions = resolutionDurations.length > 0;
    const minResolutionMs = hasResolutions ? Math.min(...resolutionDurations) : undefined;
    const maxResolutionMs = hasResolutions ? Math.max(...resolutionDurations) : undefined;
    const avgResolutionMs = hasResolutions
      ? Math.round(resolutionDurations.reduce((a, b) => a + b, 0) / resolutionDurations.length)
      : undefined;

    // Region/side/trigger breakdowns (D-SS-9): symptom contributes
    // region/side/triggers, headache contributes location (folded into the
    // region bucket) + triggersUserNoted (folded into the trigger bucket).
    const regionTally = new Map<string, { value: string; count: number }>();
    const sideTally = new Map<string, { value: string; count: number }>();
    const triggerTally = new Map<string, { value: string; count: number }>();
    for (const e of g.events) {
      const p = e.payload as Record<string, unknown>;
      if (e.eventType === "symptom") {
        tallyValue(regionTally, p.region);
        tallyValue(sideTally, p.side);
        if (Array.isArray(p.triggers)) {
          for (const t of p.triggers) tallyValue(triggerTally, t);
        }
      } else {
        tallyValue(regionTally, p.location);
        if (Array.isArray(p.triggersUserNoted)) {
          for (const t of p.triggersUserNoted) tallyValue(triggerTally, t);
        }
      }
    }

    const sessionCount = sessions.length;
    const resolvedSessionCount = resolvedSessions.length;
    const ongoingSessionCount = ongoingSessions.length;
    const resolvedPct = sessionCount > 0 ? Math.round((resolvedSessionCount / sessionCount) * 100) : 0;

    rows.push({
      key: g.key,
      name: latestExtracted.name,
      eventType,
      totalCheckIns,
      sessionCount,
      resolvedSessionCount,
      ongoingSessionCount,
      resolvedPct,
      minSeverity,
      maxSeverity,
      avgSeverity,
      distinctDaysLogged: days.size,
      minResolutionMs,
      maxResolutionMs,
      avgResolutionMs,
      firstAt: g.events[0].occurredAt,
      lastAt: latest.occurredAt,
      regionBreakdown: topBreakdown(regionTally),
      sideBreakdown: topBreakdown(sideTally),
      triggerBreakdown: topBreakdown(triggerTally),
    });
  }

  // D-SS-10: sessionCount desc, tiebreak lastAt desc.
  rows.sort((a, b) => {
    if (b.sessionCount !== a.sessionCount) return b.sessionCount - a.sessionCount;
    return Date.parse(b.lastAt) - Date.parse(a.lastAt);
  });

  return rows;
}
